import React, { useState, useEffect } from "react";
import api from "../../utils/api";
import { BASE_URL, studyMaterialApi } from "../../api/api";
import { Download, FileText, ExternalLink, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const StudyMaterial = () => {
    const [studyMaterials, setStudyMaterials] = useState([]);
    const [isPreviewOpen, setIsPreviewOpen] = useState(false);
    const [previewData, setPreviewData] = useState(null);

    useEffect(() => {
        const fetchStudyMaterials = async () => {
            try {
                const response = await api.get(`${studyMaterialApi}`);
                const data = response.data;
                setStudyMaterials(data);
            } catch (error) {
                console.error("Error fetching study materials:", error);
            }
        };
        fetchStudyMaterials();
    }, []);

    // --- Preview handlers ---
    const openPreview = (material) => {
        setPreviewData(material);
        setIsPreviewOpen(true);
        document.body.style.overflow = "hidden";
    };

    const closePreview = () => {
        setIsPreviewOpen(false);
        setPreviewData(null);
        document.body.style.overflow = "auto";
    };

    // --- Download handler (for File objects) ---
    const handleDownload = (file, title) => {
        if (!file) return;

        // Create download link
        const link = document.createElement("a");
        try {
            link.href = URL.createObjectURL(file); // Use the File object directly
        } catch (e) {
            // Fallback just in case 'file' is actually a URL string from the API
            link.href = file;
        }

        link.download = title
            ? `${title.replace(/\s+/g, "_")}.${file.name?.split(".").pop() || "pdf"}`
            : file.name || "download";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // Optional: Release object URL after download
        setTimeout(() => URL.revokeObjectURL(link.href), 100);
    };

    // --- Formatting helper ---
    const stripHtmlAndTruncate = (htmlString, maxWords = 12) => {
        if (!htmlString) return { text: "", isTruncated: false };
        const strippedText = htmlString.replace(/<[^>]+>/g, "");
        const words = strippedText.split(" ");
        if (words.length > maxWords) {
            return {
                text: words.slice(0, maxWords).join(" ") + "...",
                isTruncated: true,
            };
        }
        return { text: strippedText, isTruncated: false };
    };

    // --- Animation Variants ---
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1 },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 15 },
        visible: {
            opacity: 1,
            y: 0,
            transition: { duration: 0.4, ease: "easeOut" },
        },
    };

    return (
        <div className="w-full mx-auto p-4 sm:p-6 lg:p-8">
            {/* Standardized Editorial Header */}
            <div className="mb-10">
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                  
                    <h2 className="text-3xl md:text-4xl font-black text-neutral-900 mb-2 ">
                        Study <span className="text-yellow-500">Material.</span>
                    </h2>
                    <p className="text-gray-500 text-sm md:text-base font-medium">
                        Access and download comprehensive notes, PDFs, and
                        guides.
                    </p>
                </motion.div>
            </div>

            {/* Study Material Grid */}
            <motion.div
                variants={containerVariants}
                initial="hidden"
                animate="visible"
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
            >
                {studyMaterials.map((material) => {
                    const { text, isTruncated } = stripHtmlAndTruncate(
                        material.description,
                        12,
                    );

                    return (
                        <motion.div
                            key={material.id}
                            variants={itemVariants}
                            className="group flex flex-col bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md hover:border-neutral-900 hover:-translate-y-1 transition-all duration-300 overflow-hidden h-full"
                        >
                            {/* Card Image Area */}
                            <div className="w-full h-48 bg-gray-50 border-b border-gray-100 flex items-center justify-center p-4 relative overflow-hidden">
                                {/* Using your existing asset. Added fallback icon just in case */}
                                <img
                                    src="/assets/pdf-bg.png"
                                    alt={material.title || "Study Material"}
                                    className="object-contain w-full h-full z-10 transition-transform duration-500 group-hover:scale-105"
                                    onError={(e) => {
                                        e.target.style.display = "none";
                                        e.target.nextSibling.style.display =
                                            "flex";
                                    }}
                                />
                                <div className="absolute inset-0 flex items-center justify-center text-gray-300 hidden">
                                    <FileText size={64} strokeWidth={1} />
                                </div>
                            </div>

                            {/* Card Content Area */}
                            <div className="flex flex-col flex-1 p-6">
                                <h3 className="text-lg font-bold text-neutral-900 mb-2 line-clamp-2 leading-snug group-hover:text-red-700 transition-colors duration-300">
                                    {material.title}
                                </h3>

                                <div className="text-gray-500 text-sm font-medium mb-6 flex-1">
                                    {text}
                                    {isTruncated && (
                                        <button
                                            onClick={() =>
                                                openPreview(material)
                                            }
                                            className="ml-1 text-yellow-600 font-bold hover:text-yellow-500 transition-colors inline-block"
                                        >
                                            Read More
                                        </button>
                                    )}
                                </div>

                                {/* Actions */}
                                <div className="mt-auto pt-4 border-t border-gray-100">
                                    <a
                                        href={material.file_path}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-neutral-900 text-white rounded-lg font-bold hover:bg-yellow-400 hover:text-neutral-900 transition-colors duration-300 shadow-sm"
                                    >
                                        View & Download{" "}
                                        <ExternalLink size={16} />
                                    </a>
                                </div>
                            </div>
                        </motion.div>
                    );
                })}
            </motion.div>

            {/* --- Animated Preview Modal --- */}
            <AnimatePresence>
                {isPreviewOpen && previewData && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={closePreview}
                            className="absolute inset-0 bg-neutral-900/60 backdrop-blur-sm"
                        />

                        {/* Modal Box */}
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95, y: 20 }}
                            animate={{ opacity: 1, scale: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95, y: 20 }}
                            transition={{
                                type: "spring",
                                duration: 0.5,
                                bounce: 0,
                            }}
                            className="relative w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {/* Modal Header */}
                            <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50">
                                <h3 className="text-xl font-black text-neutral-900 pr-8">
                                    {previewData.title}
                                </h3>
                                <button
                                    onClick={closePreview}
                                    className="absolute top-6 right-6 p-1 text-gray-400 hover:text-red-700 hover:bg-red-50 rounded-md transition-colors"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Modal Body */}
                            <div className="p-6 overflow-y-auto">
                                <h4 className="text-sm font-bold text-neutral-900 uppercase tracking-wide mb-3">
                                    Description
                                </h4>
                                <div className="text-gray-600 font-medium leading-relaxed whitespace-pre-wrap">
                                    {previewData.description
                                        ? previewData.description.replace(
                                              /<[^>]+>/g,
                                              "",
                                          )
                                        : "No description provided."}
                                </div>
                            </div>

                            {/* Modal Footer (Only shows if pdf exists for download) */}
                            {previewData.pdf && (
                                <div className="p-6 border-t border-gray-100 bg-gray-50 flex justify-end">
                                    <button
                                        onClick={() =>
                                            handleDownload(
                                                previewData.pdf,
                                                previewData.title,
                                            )
                                        }
                                        className="flex items-center gap-2 px-6 py-3 bg-neutral-900 text-white rounded-lg font-bold hover:bg-yellow-400 hover:text-neutral-900 transition-all duration-300 shadow-sm"
                                    >
                                        <Download size={18} />
                                        Download File
                                    </button>
                                </div>
                            )}
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default StudyMaterial;
